import { useEffect, useRef } from 'react';

import { useTypewriterAutoScroll } from './hooks/useTypewriterAutoScroll';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { AiToolResult } from './types';

import Icon from '@/components/base/Icon';
import {
    InfiniteScroll,
    type InfiniteScrollRef
} from '@/components/composite/InfiniteScroll/InfiniteScroll';

import { SparksMessageBubble } from './SparksMessageBubble';
import { type SparksIntent } from './types';

export type SparksMessage = {
    id: string;
    role: 'user' | 'agent';
    content: string;
    tool_results?: AiToolResult[];
    created_at?: string;
    intent?: SparksIntent;
};

type SparksConversationProps = {
    messages: SparksMessage[];
    isLoading?: boolean;
    isSending?: boolean;
    hasMore?: boolean;
    loadMore?: () => void;
    isLoadingMore?: boolean;
    /** AC05: Error message displayed in the conversation when response generation fails */
    error?: string | null;
    onRetry?: () => void;
    /** Assistant message id that should play the typing reveal (latest reply after send). */
    typewriterMessageId?: string | null;
    onTypewriterComplete?: (messageId: string) => void;
};

export function SparksConversation({
    messages,
    isLoading = false,
    isSending = false,
    hasMore = false,
    loadMore,
    isLoadingMore = false,
    error = null,
    onRetry,
    typewriterMessageId = null,
    onTypewriterComplete
}: SparksConversationProps) {
    const { t } = useTranslation('sparks');
    const scrollRef = useRef<InfiniteScrollRef>(null);
    const previousLastMessageIdRef = useRef<string | null>(null);

    const { handleTypewriterComplete } = useTypewriterAutoScroll({
        scrollRef,
        typewriterMessageId,
        onTypewriterComplete
    });

    useEffect(() => {
        const lastMessageId = messages[messages.length - 1]?.id ?? null;
        const isFirstObservedMessageSet =
            messages.length > 0 && previousLastMessageIdRef.current === null;
        const hasNewestMessageChanged =
            messages.length > 0 &&
            previousLastMessageIdRef.current !== null &&
            previousLastMessageIdRef.current !== lastMessageId;
        const shouldScrollToBottom =
            Boolean(isSending || error) || isFirstObservedMessageSet || hasNewestMessageChanged;

        if (shouldScrollToBottom) {
            requestAnimationFrame(() => {
                scrollRef.current?.scrollToBottom();
            });
        }

        previousLastMessageIdRef.current = lastMessageId;
    }, [messages, isSending, error]);

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3.5">
            {isLoading && messages.length === 0 ? (
                <div className="flex flex-1 items-center justify-center py-8 font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('loading_initial')}
                </div>
            ) : (
                <>
                    <InfiniteScroll
                        ref={scrollRef}
                        className="min-h-0 flex-1"
                        data={messages}
                        getItemKey={(index) => messages[index]?.id ?? index}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        isLoadingMore={isLoadingMore}
                        contentAlignment="bottom"
                        renderItem={(message) => (
                            <div className="min-w-0 max-w-full py-1.5">
                                <SparksMessageBubble
                                    message={message}
                                    animateTyping={message.id === typewriterMessageId}
                                    onTypingComplete={() => handleTypewriterComplete(message.id)}
                                />
                            </div>
                        )}
                        inverse
                    />

                    {isSending && (
                        <div
                            className="flex items-center gap-2 py-1.5"
                            role="status"
                            aria-live="polite"
                            aria-label={t('agent_typing')}>
                            <div className="flex items-center gap-1 rounded-xl bg-white px-3.5 py-2.5 dark:bg-icoDarkMode-anthracite">
                                <span
                                    className="size-1.5 shrink-0 animate-sparks-typing-dot rounded-full bg-icoGray-400 dark:bg-icoDarkMode-wolf"
                                    style={{ animationDelay: '0ms' }}
                                />
                                <span
                                    className="size-1.5 shrink-0 animate-sparks-typing-dot rounded-full bg-icoGray-400 dark:bg-icoDarkMode-wolf"
                                    style={{ animationDelay: '150ms' }}
                                />
                                <span
                                    className="size-1.5 shrink-0 animate-sparks-typing-dot rounded-full bg-icoGray-400 dark:bg-icoDarkMode-wolf"
                                    style={{ animationDelay: '300ms' }}
                                />
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="flex justify-start py-1.5">
                            <div
                                className="flex w-full items-center gap-5 rounded-md border border-icoRed-200 bg-icoRed-100 px-3 py-3 dark:border-icoRed dark:bg-icoRed/20"
                                role="alert">
                                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                                    <Icon
                                        icon="warning"
                                        className="size-3.5 shrink-0 text-icoRed"
                                    />
                                    <p className="font-GMedium text-xs leading-[14px] text-icoRed">
                                        {error}
                                    </p>
                                </div>

                                {onRetry && (
                                    <button
                                        type="button"
                                        onClick={onRetry}
                                        className="shrink-0 rounded bg-icoRed px-2 py-1.5 font-GSemiBold text-[10px] leading-3 text-white transition-opacity hover:bg-icoRed-400">
                                        {t('retry')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
